import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Trash2, Plus, Target, BookOpen, Sparkles, Heart, Lightbulb, PenLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getJournalEntries,
  createJournalEntry,
  deleteJournalEntry,
  getJournalReflection,
  getGoals,
  createGoal,
  updateGoal,
  deleteGoal,
} from "@/data/requests/practice-requests";

function HowItWorks({ t, ns }) {
  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-sm">{t(`${ns}.howTitle`)}</h3>
        </div>
        <ol className="space-y-2">
          {[1, 2, 3].map((n) => (
            <li key={n} className="flex items-start gap-3 text-sm text-muted-foreground">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                {n}
              </span>
              {t(`${ns}.how${n}`)}
            </li>
          ))}
        </ol>
        <p className="mt-3 text-xs italic text-muted-foreground">{t(`${ns}.howReassure`)}</p>
      </CardContent>
    </Card>
  );
}

export function JournalScreen() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [goalText, setGoalText] = useState("");
  const [entryContent, setEntryContent] = useState("");
  const [mood, setMood] = useState("steady");

  const { data: goalsData, isLoading: goalsLoading } = useQuery({
    queryKey: ["goals"],
    queryFn: getGoals,
  });
  const { data: entriesData, isLoading: entriesLoading } = useQuery({
    queryKey: ["journal"],
    queryFn: getJournalEntries,
  });
  const entryCount = entriesData?.data?.length || 0;
  const { data: reflectionData, isLoading: reflectionLoading } = useQuery({
    queryKey: ["journal-reflection", i18n.language, entryCount],
    queryFn: () => getJournalReflection(i18n.language),
    enabled: entryCount >= 2,
  });
  const reflection = reflectionData?.data?.reflection;

  const invalidate = (key) => queryClient.invalidateQueries({ queryKey: [key] });
  const onError = (err) =>
    toast({
      title: "Something went wrong",
      description: err?.response?.data?.error || "Please try again.",
      variant: "destructive",
    });

  const addGoal = useMutation({
    mutationFn: () => createGoal(goalText.trim()),
    onSuccess: () => {
      setGoalText("");
      invalidate("goals");
    },
    onError,
  });
  const toggleGoal = useMutation({
    mutationFn: ({ id, achieved }) => updateGoal(id, achieved),
    onSuccess: () => invalidate("goals"),
    onError,
  });
  const removeGoal = useMutation({
    mutationFn: deleteGoal,
    onSuccess: () => invalidate("goals"),
    onError,
  });
  const addEntry = useMutation({
    mutationFn: () => {
      const moodTag = `[${t(`journalx.moods.${mood}`)}] `;
      return createJournalEntry({ content: moodTag + entryContent.trim() });
    },
    onSuccess: () => {
      setEntryContent("");
      setMood("steady");
      invalidate("journal");
      invalidate("journal-reflection");
    },
    onError,
  });
  const removeEntry = useMutation({
    mutationFn: deleteJournalEntry,
    onSuccess: () => {
      invalidate("journal");
      invalidate("journal-reflection");
    },
    onError,
  });

  const goals = goalsData?.data || [];
  const entries = entriesData?.data || [];

  return (
    <AppLayout
      title="Journal & Goals"
      description="Track what you're working on in your relationships and reflect on your practice."
      loading={goalsLoading || entriesLoading}
      hideHeader
    >
      <div className="relative overflow-hidden rounded-[2.25rem] p-4 md:p-7" style={{ background: "radial-gradient(circle at 88% 2%, rgba(21,156,120,0.13), transparent 28%), linear-gradient(180deg, #F1FCF8 0%, #FFFFFF 48%, #F6F5FF 100%)" }}>
        <div className="relative mb-5 flex min-h-[160px] items-center overflow-hidden rounded-[2rem] p-6 text-white shadow-[0_20px_46px_rgba(15,111,90,0.2)] md:p-7" style={{ background: "linear-gradient(135deg, #0F6F5A 0%, #159C78 58%, #32B995 100%)" }}>
          <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10" />
          <div className="relative flex h-16 w-16 -rotate-6 items-center justify-center rounded-2xl border border-white/25 bg-white/15"><BookOpen className="h-8 w-8" /></div>
          <div className="relative ml-5 flex-1">
            <p className="text-xs font-extrabold uppercase tracking-[0.15em] text-white/65">Journal</p>
            <h1 className="mt-1 text-3xl font-extrabold">{t("journalx.prompts.next")}</h1>
          </div>
          <div className="relative grid grid-cols-2 gap-2">
            <div className="rounded-2xl bg-white/15 px-4 py-3 text-center"><strong className="block text-2xl">{entryCount}</strong><span className="text-[10px] font-bold text-white/65">notes</span></div>
            <div className="rounded-2xl bg-white/15 px-4 py-3 text-center"><strong className="block text-2xl">{goals.length}</strong><span className="text-[10px] font-bold text-white/65">goals</span></div>
          </div>
        </div>

        <div className="relative grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(310px,0.85fr)]">
          <div className="space-y-4">
            <Card className="rounded-[1.75rem] border-[#DCEFE9] bg-white/90 shadow-[0_16px_35px_rgba(15,111,90,0.08)]">
              <CardContent className="space-y-4 p-5 md:p-6">
                <div className="flex items-center justify-between"><div className="flex items-center gap-2"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#159C78] text-white"><PenLine className="h-5 w-5" /></div><h2 className="font-extrabold">Journal</h2></div><details className="text-xs text-muted-foreground"><summary className="cursor-pointer font-bold">{t("journalx.howTitle")}</summary><div className="mt-3"><HowItWorks t={t} ns="journalx" /></div></details></div>

                <div className="grid grid-cols-3 gap-2">
                  {[{ key: "heavy", color: "#F04461" }, { key: "steady", color: "#5865F2" }, { key: "clear", color: "#159C78" }].map(({ key, color }) => (
                    <button key={key} type="button" onClick={() => setMood(key)} className="rounded-full border px-3 py-2 text-sm font-bold transition" style={{ backgroundColor: mood === key ? color : "#fff", borderColor: mood === key ? color : "#E8EAF0", color: mood === key ? "#fff" : "#64748B" }}>{t(`journalx.moods.${key}`)}</button>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {[{ key: "felt", Icon: Heart, color: "#F04461", bg: "#FFF0F3" }, { key: "needed", Icon: Lightbulb, color: "#F59E0B", bg: "#FFF6DE" }, { key: "next", Icon: PenLine, color: "#5865F2", bg: "#EEF0FF" }].map(({ key, Icon, color, bg }) => (
                    <button key={key} type="button" onClick={() => setEntryContent((c) => `${c}${c ? "\n\n" : ""}${t(`journalx.prompts.${key}`)} `)} className="flex min-h-[96px] flex-col justify-between rounded-2xl p-3 text-left text-xs font-extrabold transition hover:-translate-y-0.5" style={{ backgroundColor: bg }}><span className="flex h-8 w-8 items-center justify-center rounded-xl text-white" style={{ backgroundColor: color }}><Icon className="h-4 w-4" /></span>{t(`journalx.prompts.${key}`)}</button>
                  ))}
                </div>

                <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (entryContent.trim()) addEntry.mutate(); }}>
                  <Textarea value={entryContent} onChange={(e) => setEntryContent(e.target.value)} placeholder={t("journalx.placeholder")} rows={7} maxLength={5000} className="resize-none rounded-2xl border-[#DCEFE9] bg-[#FBFEFD] p-4 text-base" />
                  <Button type="submit" className="h-12 w-full rounded-2xl bg-[#159C78] text-white hover:bg-[#128568]" disabled={addEntry.isPending || !entryContent.trim()}>{t("journalx.save")}</Button>
                </form>
              </CardContent>
            </Card>

            {entryCount >= 2 && <div className="rounded-[1.5rem] border border-[#D9DEFF] bg-[#EEF0FF] p-5"><div className="mb-2 flex items-center gap-2 text-[#5865F2]"><Sparkles className="h-5 w-5" /><h3 className="text-sm font-extrabold">{t("journalx.reflectionTitle")}</h3></div><p className="text-sm leading-relaxed text-[#1E293B]">{reflectionLoading ? "…" : reflection || t("journalx.reflectionLocked")}</p></div>}
          </div>

          <div className="space-y-4">
            <Card className="rounded-[1.75rem] border-[#DCEFE9] bg-[#E9FBF5]">
              <CardContent className="p-5">
                <div className="mb-4 flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#159C78] text-white"><Target className="h-5 w-5" /></div><div><h2 className="font-extrabold">Communication goals</h2><p className="text-xs text-muted-foreground">Small and concrete.</p></div></div>
                <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (goalText.trim()) addGoal.mutate(); }}><Input value={goalText} onChange={(e) => setGoalText(e.target.value)} placeholder="Say no without over-explaining" maxLength={300} className="rounded-xl bg-white" /><Button type="submit" className="rounded-xl bg-[#159C78]" disabled={addGoal.isPending || !goalText.trim()}><Plus className="h-4 w-4" /></Button></form>
                <div className="mt-3 space-y-2">{goals.length === 0 && <p className="text-sm text-muted-foreground">No goals yet.</p>}{goals.map((goal) => <div key={goal.id} className="flex items-center gap-3 rounded-2xl bg-white/85 p-3"><Checkbox checked={goal.achieved} onCheckedChange={(checked) => toggleGoal.mutate({ id: goal.id, achieved: !!checked })} /><span className={`flex-1 text-sm font-medium ${goal.achieved ? "line-through text-gray-400" : ""}`}>{goal.text}</span><button onClick={() => removeGoal.mutate(goal.id)} className="text-gray-400 hover:text-red-500" aria-label="Delete goal"><Trash2 className="h-4 w-4" /></button></div>)}</div>
              </CardContent>
            </Card>

            <Card className="rounded-[1.75rem] border-[#E8EAF0] bg-white/90">
              <CardContent className="p-5"><div className="mb-4 flex items-center justify-between"><h2 className="font-extrabold">Recent notes</h2><span className="rounded-full bg-[#EEF0FF] px-2.5 py-1 text-xs font-bold text-[#5865F2]">{entryCount}</span></div><div className="space-y-3">{entries.length === 0 && <p className="text-sm text-muted-foreground">Your saved notes will appear here.</p>}{entries.map((entry) => <div key={entry.id} className="rounded-2xl border border-[#E8EAF0] p-3"><div className="flex items-start justify-between"><p className="text-xs font-bold text-muted-foreground">{new Date(entry.createdAt).toLocaleDateString()}</p><button onClick={() => removeEntry.mutate(entry.id)} className="text-gray-400 hover:text-red-500" aria-label="Delete entry"><Trash2 className="h-4 w-4" /></button></div><p className="mt-2 line-clamp-4 whitespace-pre-line text-sm leading-relaxed">{entry.content}</p></div>)}</div></CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
